import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Country } from "country-state-city";
import { InfoCircle } from "iconsax-reactjs";

import OnboardingLayout from "component/auth/OnboardingLayout";
import SampleDocumentModal from "component/auth/onboarding/SampleDocumentModal";
import UploadInputField from "component/auth/onboarding/UploadInputField";
import { identificationSampleDocuments } from "component/auth/onboarding/onboardingSampleDocuments";
import Dropdown from "component/form/dropDown";
import Input from "component/form/Input";
import InfoTooltip from "component/InfoTooltip";
import Loader from "component/loader";
import ErrorMessage from "pages/auth_v2/ErrorMessage";
import { CheckCircleIcon } from "asset/icons";
import {
    handleAddBusinessUbo,
    handleFetchBusinessById,
    handleFetchKybCountries,
    handleUploadOnboardingDocument,
} from "api/onboarding";
import { fetchBusinessStates, updateDirectorDetails } from "api/fetchers";

const steps = [{ title: "Control person" }, { title: "Additional information" }];

const CONTROL_PERSON_MODES = {
    EXISTING: "existing",
    NEW: "new",
};

const LOCKED_FIELD_MESSAGE = "This information was previously submitted and can’t be changed.";

const LockedField = ({ locked, children }) => (
    <div
        title={locked ? LOCKED_FIELD_MESSAGE : undefined}
        aria-disabled={locked || undefined}
        className={locked ? "cursor-not-allowed [&_*]:!cursor-not-allowed" : undefined}
    >
        {children}
    </div>
);

const genderOptions = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
];

const identificationOptions = [
    { label: "Passport", value: "passport" },
    { label: "National ID", value: "national_id" },
    { label: "Driver’s licence", value: "drivers_license" },
    { label: "Voter’s card", value: "voters_card" },
];

const createInitialForm = (phoneCountry = "") => ({
    firstName: "",
    lastName: "",
    email: "",
    phoneCountry,
    phoneNumber: "",
    title: "",
    ownershipShare: "",
    bvn: "",
    gender: "",
    dateOfBirth: "",
    identificationType: "passport",
    identificationNumber: "",
    identificationDocumentFront: null,
    identificationDocumentBack: null,
    streetAddress: "",
    city: "",
    state: "",
    postalCode: "",
    isDirector: true,
});

const normalizeCountriesResponse = (responseBody) =>
    (Array.isArray(responseBody?.data) && responseBody.data) ||
    (Array.isArray(responseBody?.data?.countries) && responseBody.data.countries) ||
    (Array.isArray(responseBody?.countries) && responseBody.countries) ||
    (Array.isArray(responseBody) && responseBody) ||
    [];

const getBusinessCountryCode = (business = {}) => {
    if (business?.businessCountry?.code) return String(business.businessCountry.code).toUpperCase();
    if (business?.countryCode) return String(business.countryCode).toUpperCase();

    const countryName = business?.businessCountry?.name || business?.country;
    return (
        Country.getAllCountries().find(
            (country) =>
                country.name.toLowerCase() ===
                String(countryName || "")
                    .trim()
                    .toLowerCase()
        )?.isoCode || ""
    );
};

const getUploadUrl = (raw) => {
    const candidates = [
        raw,
        raw?.url,
        raw?.url?.link,
        raw?.link,
        raw?.data?.url,
        raw?.data?.url?.link,
        raw?.data?.link,
    ];

    return candidates.find((value) => typeof value === "string" && value.trim())?.trim() || "";
};

const getUploadKey = (raw, fallback = "") =>
    raw?.key || raw?.fileKey || raw?.data?.key || raw?.data?.fileKey || fallback;

const isMongoObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || "").trim());

const getDirectorMongoId = (director = {}) => {
    const directorId = director._id ?? director.id ?? director.directorId;
    const normalizedId = String(directorId || "").trim();
    return isMongoObjectId(normalizedId) ? normalizedId : "";
};

const getDirectorSelectionKey = (director = {}) => {
    const directorId = getDirectorMongoId(director);
    if (directorId) {
        return `id:${directorId}`;
    }

    const bvn = director.bvnNumber || director.bvn;
    if (bvn) return `bvn:${bvn}`;

    const email = String(director.email || "")
        .trim()
        .toLowerCase();
    return email ? `email:${email}` : "";
};

const getRequirementDirectors = (requirements = []) =>
    requirements.flatMap((requirement) =>
        Array.isArray(requirement?.directors) ? requirement.directors : []
    );

const getDirectorIdentityKey = (director = {}) => {
    const bvn = director.bvnNumber || director.bvn;
    if (bvn) return `bvn:${bvn}`;

    const email = String(director.email || "")
        .trim()
        .toLowerCase();
    if (email) return `email:${email}`;

    const name = `${director.firstName || ""} ${director.lastName || ""}`.trim().toLowerCase();
    return name ? `name:${name}` : getDirectorMongoId(director);
};

const mergeDirectorsByIdentity = (primaryDirectors = [], secondaryDirectors = []) => {
    const merged = new Map();

    primaryDirectors.forEach((director) => {
        const directorId = getDirectorMongoId(director);
        if (!directorId) return;
        const identityKey = getDirectorIdentityKey(director);

        merged.set(identityKey, {
            ...director,
            _id: directorId,
        });
    });

    secondaryDirectors.forEach((director) => {
        const directorId = getDirectorMongoId(director);
        if (!directorId) return;
        const identityKey = getDirectorIdentityKey(director);
        const existingDirector = merged.get(identityKey);

        merged.set(identityKey, {
            ...director,
            ...(existingDirector || {}),
            _id: existingDirector?._id || directorId,
        });
    });

    return Array.from(merged.values());
};

const asExistingFile = (raw, fallbackName) => {
    const url = getUploadUrl(raw);
    if (!url) return null;
    return {
        name: raw?.name || raw?.key || fallbackName,
        size: raw?.size || 0,
        url,
        key: raw?.key || "",
    };
};

const fromApiIdentificationType = (type) =>
    ({
        passport: "passport",
        currentpassport: "passport",
        nationalid: "national_id",
        national_id: "national_id",
        driverslicense: "drivers_license",
        driverlicense: "drivers_license",
        drivers_license: "drivers_license",
        voterscard: "voters_card",
        voters_card: "voters_card",
    })[
        String(type || "")
            .replace(/[\s-]/g, "")
            .toLowerCase()
    ] || "passport";

const toApiIdentificationType = (type) =>
    ({
        passport: "passport",
        national_id: "nationalId",
        drivers_license: "driversLicense",
        voters_card: "votersCard",
    })[type] || type;

const toDirectorIdentificationType = (type) =>
    ({
        passport: "currentPassport",
        national_id: "nationalId",
        drivers_license: "driverLicense",
        voters_card: "IdCard",
    })[type] || type;

const mapPersonToForm = (person = {}, fallbackCountry = "") => ({
    ...createInitialForm(fallbackCountry),
    firstName: person.firstName || "",
    lastName: person.lastName || "",
    email: person.email || "",
    phoneCountry: person.phoneCountry || fallbackCountry,
    phoneNumber: String(person.phoneNumber || "").replace(/^\+/, ""),
    title: person.title || "",
    ownershipShare: String(person.percentageOwnership || person.ownershipShare || ""),
    bvn: person.bvnNumber || person.bvn || "",
    gender: String(person.gender || "").toLowerCase(),
    dateOfBirth: String(person.dateOfBirth || "").slice(0, 10),
    identificationType: fromApiIdentificationType(
        person.meanOfIdentificationType || person.meansOfIdentification
    ),
    identificationNumber:
        person.idNumber || person.meanOfIdentificationNumber || person.identificationNumber || "",
    identificationDocumentFront: asExistingFile(
        person.idDocumentFront || person.meanOfIdentificationUrl || person.identificationDocument,
        "Identification front"
    ),
    identificationDocumentBack: asExistingFile(person.idDocumentBack, "Identification back"),
    streetAddress:
        person.address?.street ||
        person.residentialAddress?.street ||
        person.residentialAddress?.streetAddress ||
        person.streetAddress ||
        "",
    city: person.address?.city || person.residentialAddress?.city || person.city || "",
    state: person.address?.state || person.residentialAddress?.state || person.state || "",
    postalCode:
        person.address?.zipCode ||
        person.residentialAddress?.postalCode ||
        person.residentialAddress?.zipCode ||
        person.postalCode ||
        "",
    isDirector: person.isDirector ?? true,
});

const hasSubmittedValue = (...values) =>
    values.some(
        (value) =>
            value !== undefined &&
            value !== null &&
            (typeof value !== "string" || value.trim() !== "")
    );

const getSubmittedPersonFields = (person = {}) => {
    const residentialAddress = person.residentialAddress || {};
    const address = person.address || {};
    const submittedFields = {
        firstName: hasSubmittedValue(person.firstName),
        lastName: hasSubmittedValue(person.lastName),
        email: hasSubmittedValue(person.email),
        phoneCountry: hasSubmittedValue(person.phoneCountry, person.phoneNumber),
        phoneNumber: hasSubmittedValue(person.phoneNumber),
        title: hasSubmittedValue(person.title),
        ownershipShare: hasSubmittedValue(person.percentageOwnership, person.ownershipShare),
        bvn: hasSubmittedValue(person.bvnNumber, person.bvn),
        gender: hasSubmittedValue(person.gender),
        dateOfBirth: hasSubmittedValue(person.dateOfBirth),
        identificationType: hasSubmittedValue(
            person.meanOfIdentificationType,
            person.meansOfIdentification
        ),
        identificationNumber: hasSubmittedValue(
            person.idNumber,
            person.meanOfIdentificationNumber,
            person.identificationNumber
        ),
        identificationDocumentFront: Boolean(
            getUploadUrl(
                person.idDocumentFront ||
                    person.meanOfIdentificationUrl ||
                    person.identificationDocument
            )
        ),
        identificationDocumentBack: Boolean(getUploadUrl(person.idDocumentBack)),
        streetAddress: hasSubmittedValue(
            address.street,
            residentialAddress.street,
            residentialAddress.streetAddress,
            person.streetAddress
        ),
        city: hasSubmittedValue(address.city, residentialAddress.city, person.city),
        state: hasSubmittedValue(address.state, residentialAddress.state, person.state),
        postalCode: hasSubmittedValue(
            address.zipCode,
            residentialAddress.postalCode,
            residentialAddress.zipCode,
            person.postalCode
        ),
    };

    return Object.keys(submittedFields).filter((field) => submittedFields[field]);
};

const normalizeOwnershipInput = (value) => {
    const cleaned = String(value || "").replace(/[^\d.]/g, "");
    const [whole, ...decimals] = cleaned.split(".");
    return decimals.length ? `${whole}.${decimals.join("")}` : whole;
};

const parseOwnership = (value) => {
    const ownership = Number(String(value || "").replace(/[,%\s]/g, ""));
    return Number.isFinite(ownership) ? ownership : 0;
};

const extractHouseNumber = (street = "") => {
    const match = street.trim().match(/^(\d+[A-Za-z-]?)/);
    return match ? match[1] : "";
};

function BusinessContact({ currentStep = 0, onNext, onBack }) {
    const navigate = useNavigate();
    const { business, businessid } = useSelector((state) => state);
    const businessId = businessid || business?._id;
    const reduxCountryCode = getBusinessCountryCode(business);

    const [mode, setMode] = useState(CONTROL_PERSON_MODES.EXISTING);
    const [directors, setDirectors] = useState([]);
    const [ubos, setUbos] = useState([]);
    const [selectedDirectorKey, setSelectedDirectorKey] = useState("");
    const [businessCountryCode, setBusinessCountryCode] = useState(reduxCountryCode);
    const [phoneCountryOptions, setPhoneCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [form, setForm] = useState(() => createInitialForm(reduxCountryCode));
    const [lockedFields, setLockedFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingSide, setUploadingSide] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);

    const selectedDirector = useMemo(
        () =>
            directors.find((director) => getDirectorSelectionKey(director) === selectedDirectorKey),
        [directors, selectedDirectorKey]
    );

    const directorOptions = useMemo(
        () =>
            directors
                .map((director) => ({
                    value: getDirectorSelectionKey(director),
                    label: `${director.firstName || ""} ${director.lastName || ""}`.trim(),
                }))
                .filter((option) => option.value),
        [directors]
    );

    const selectedPhoneCountry = useMemo(
        () => phoneCountryOptions.find((option) => option.code === form.phoneCountry),
        [form.phoneCountry, phoneCountryOptions]
    );

    const requiresBvn = String(businessCountryCode).toUpperCase() === "NG";
    const ownership = parseOwnership(form.ownershipShare);
    const existingOwnershipTotal = ubos.reduce(
        (total, ubo) =>
            total + parseOwnership(ubo.percentageOwnership || ubo.ownershipShare || "0"),
        0
    );
    const availableOwnership = Math.max(0, 100 - existingOwnershipTotal);
    const ownershipBelowMinimum = Boolean(form.ownershipShare) && ownership < 25;
    const ownershipAboveMaximum = Boolean(form.ownershipShare) && ownership > 100;
    const ownershipExceedsLimit =
        mode === CONTROL_PERSON_MODES.NEW &&
        Boolean(form.ownershipShare) &&
        existingOwnershipTotal + ownership > 100;
    const ownershipError = ownershipBelowMinimum
        ? "Ownership share must be at least 25%."
        : ownershipAboveMaximum
          ? "Ownership share cannot be more than 100%."
          : ownershipExceedsLimit
            ? "Total UBO ownership cannot exceed 100%."
            : "";
    const needsBackDocument = form.identificationType !== "passport";
    const isNewControlPerson = mode === CONTROL_PERSON_MODES.NEW;
    const hasFrontDocument = Boolean(getUploadUrl(form.identificationDocumentFront));
    const hasBackDocument = Boolean(getUploadUrl(form.identificationDocumentBack));
    const formVisible = mode === CONTROL_PERSON_MODES.NEW || Boolean(selectedDirector);
    const isFieldLocked = (field) =>
        mode === CONTROL_PERSON_MODES.EXISTING && lockedFields.includes(field);

    const isFormValid = Boolean(
        formVisible &&
        form.firstName.trim() &&
        form.lastName.trim() &&
        /^\S+@\S+\.\S+$/.test(form.email.trim()) &&
        form.phoneCountry &&
        form.phoneNumber.trim() &&
        form.title.trim() &&
        form.ownershipShare &&
        !ownershipError &&
        (!requiresBvn || /^\d{11}$/.test(form.bvn)) &&
        form.gender &&
        form.dateOfBirth &&
        form.identificationType &&
        form.identificationNumber.trim() &&
        hasFrontDocument &&
        (!needsBackDocument || hasBackDocument) &&
        form.streetAddress.trim() &&
        form.city.trim() &&
        form.state &&
        form.postalCode.trim()
    );

    const loadControlPersonData = useCallback(async () => {
        if (!businessId) {
            setLoading(false);
            setErrorMessage("Unable to find your business. Please refresh and try again.");
            return;
        }

        try {
            setLoading(true);
            const [businessResponse, countriesResponse, statesResponse] = await Promise.all([
                handleFetchBusinessById(businessId),
                handleFetchKybCountries(),
                fetchBusinessStates({ businessId, normalize: true }),
            ]);

            const businessRecord = businessResponse?.business || businessResponse || {};
            const countryCode = getBusinessCountryCode(businessRecord) || reduxCountryCode;
            const mappedDirectors = mergeDirectorsByIdentity(
                Array.isArray(businessRecord.directors) ? businessRecord.directors : [],
                getRequirementDirectors(businessRecord.kycRequirements)
            );

            const countryOptions = normalizeCountriesResponse(countriesResponse)
                .map((item) => {
                    const code = String(item.code || "").toUpperCase();
                    const countryMeta = Country.getCountryByCode(code);
                    const phoneCode =
                        item.phoneCode ||
                        item.phonecode ||
                        item.phone?.[0] ||
                        countryMeta?.phonecode;
                    if (!code || !phoneCode) return null;
                    const dialCode = String(phoneCode).startsWith("+")
                        ? String(phoneCode)
                        : `+${phoneCode}`;
                    return {
                        value: code,
                        code,
                        dialCode,
                        searchLabel: `${item.name || countryMeta?.name || ""} ${code} ${dialCode}`,
                        displayValue: code,
                        label: (
                            <span className="flex w-full items-center gap-2">
                                <img
                                    src={item.imageUrl}
                                    alt={`${item.name || countryMeta?.name} flag`}
                                    className="size-5 rounded-full object-cover"
                                />
                                <span>{code}</span>
                                <span className="ml-auto text-sm text-[#6F739B]">{dialCode}</span>
                            </span>
                        ),
                    };
                })
                .filter(Boolean);

            setBusinessCountryCode(countryCode);
            setDirectors(mappedDirectors);
            setUbos(Array.isArray(businessRecord.UBOs) ? businessRecord.UBOs : []);
            setPhoneCountryOptions(countryOptions);
            setStateOptions(Array.isArray(statesResponse) ? statesResponse : []);
            setForm(createInitialForm(countryCode));
        } catch (error) {
            setErrorMessage(error?.message || "Unable to load control-person information.");
        } finally {
            setLoading(false);
        }
    }, [businessId, reduxCountryCode]);

    useEffect(() => {
        loadControlPersonData();
    }, [loadControlPersonData]);

    useEffect(() => {
        if (!selectedDirector) return;
        const mappedForm = mapPersonToForm(selectedDirector, businessCountryCode);
        const normalizedPhone = String(mappedForm.phoneNumber || "").replace(/[^\d]/g, "");
        const matchingCountry = [...phoneCountryOptions]
            .sort((first, second) => second.dialCode.length - first.dialCode.length)
            .find((option) =>
                normalizedPhone.startsWith(String(option.dialCode).replace(/^\+/, ""))
            );

        setForm({
            ...mappedForm,
            ...(matchingCountry
                ? {
                      phoneCountry: matchingCountry.code,
                      phoneNumber: normalizedPhone.slice(
                          String(matchingCountry.dialCode).replace(/^\+/, "").length
                      ),
                  }
                : { phoneNumber: normalizedPhone }),
        });
        setLockedFields(getSubmittedPersonFields(selectedDirector));
        setErrorMessage("");
    }, [businessCountryCode, phoneCountryOptions, selectedDirector]);

    const updateField = (field, value) => {
        if (isFieldLocked(field)) return;

        setForm((current) => ({
            ...current,
            [field]: value,
            ...(field === "identificationType"
                ? {
                      identificationDocumentFront: null,
                      identificationDocumentBack: null,
                  }
                : {}),
        }));
        setErrorMessage("");
    };

    const selectMode = (nextMode) => {
        setMode(nextMode);
        setSelectedDirectorKey("");
        setForm(createInitialForm(businessCountryCode));
        setLockedFields([]);
        setErrorMessage("");
    };

    const handleDocumentUpload = async (event, side) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const field =
            side === "back" ? "identificationDocumentBack" : "identificationDocumentFront";

        try {
            setUploadingSide(side);
            const response = await handleUploadOnboardingDocument(file);
            const uploadData = response?.data || response;
            const url = getUploadUrl(uploadData);
            if (!url) throw new Error("No upload URL returned");

            updateField(field, {
                name: file.name,
                size: file.size,
                url,
                key: getUploadKey(uploadData, file.name),
            });
        } catch (error) {
            setErrorMessage(error?.message || "Document upload failed. Please try again.");
        } finally {
            setUploadingSide("");
            event.target.value = "";
        }
    };

    const buildPayload = () => {
        const cleanPhone = String(form.phoneNumber).replace(/[^\d]/g, "");
        const frontUrl = getUploadUrl(form.identificationDocumentFront);
        const backUrl = getUploadUrl(form.identificationDocumentBack);
        const selectedState = stateOptions.find((option) => option.value === form.state);

        const commonPayload = {
            ...(requiresBvn && form.bvn ? { bvn: form.bvn } : {}),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            phoneNumber: cleanPhone ? `${selectedPhoneCountry?.dialCode || ""}${cleanPhone}` : "",
            title: form.title.trim(),
            percentageOwnership: String(form.ownershipShare),
            gender: form.gender === "male" ? "Male" : "Female",
            dateOfBirth: form.dateOfBirth,
            isDirector: isNewControlPerson ? form.isDirector : true,
            address: {
                houseNumber: extractHouseNumber(form.streetAddress),
                street: form.streetAddress.trim(),
                city: form.city.trim(),
                state: selectedState?.label || form.state,
                zipCode: form.postalCode.trim(),
                subdivision: selectedState?.code || "",
            },
        };

        return {
            ...commonPayload,
            meanOfIdentificationType: toApiIdentificationType(form.identificationType),
            meanOfIdentificationUrl: frontUrl,
            idNumber: form.identificationNumber.trim(),
            idDocumentFront: {
                url: frontUrl,
                key: getUploadKey(form.identificationDocumentFront),
            },
            ...(backUrl
                ? {
                      idDocumentBack: {
                          url: backUrl,
                          key: getUploadKey(form.identificationDocumentBack),
                      },
                  }
                : {}),
        };
    };

    const handleContinue = async () => {
        if (!isFormValid || saving || !businessId) return;

        try {
            setSaving(true);
            const payload = buildPayload();

            if (mode === CONTROL_PERSON_MODES.EXISTING) {
                const directorId = getDirectorMongoId(selectedDirector);

                if (!directorId) {
                    throw new Error(
                        "Unable to identify the selected director. Please select again."
                    );
                }

                await updateDirectorDetails({
                    businessId,
                    body: {
                        _id: directorId,
                        email: payload.email,
                        phoneNumber: payload.phoneNumber,
                        title: payload.title,
                        percentageOwnership: payload.percentageOwnership,
                        dateOfBirth: payload.dateOfBirth,
                        gender: payload.gender,
                        meansOfIdentification: toDirectorIdentificationType(
                            form.identificationType
                        ),
                        idNumber: payload.idNumber,
                        address: payload.address,
                        ...(payload.idDocumentFront?.url
                            ? { idDocumentFront: payload.idDocumentFront }
                            : {}),
                        ...(payload.idDocumentBack?.url
                            ? { idDocumentBack: payload.idDocumentBack }
                            : {}),
                    },
                });
            } else {
                await handleAddBusinessUbo(businessId, payload);
            }

            if (onNext) onNext();
            else navigate("/update-kyc/additional-information");
        } catch (error) {
            setErrorMessage(error?.message || "Unable to save the control person.");
        } finally {
            setSaving(false);
        }
    };

    const selectedIdentificationLabel =
        identificationOptions.find((option) => option.value === form.identificationType)?.label ||
        "document";

    return (
        <OnboardingLayout
            title="Add USD wallet"
            steps={steps}
            currentStep={currentStep}
            showSkip={false}
        >
            <div className="mx-auto w-full max-w-[820px]">
                <div className="mb-8 flex items-center gap-2">
                    <h1 className="aku-heading-h2-medium text-[#101828]">
                        Add control person (UBO)
                    </h1>
                    <InfoTooltip
                        message="The Ultimate Beneficial Owner is the person who ultimately owns or controls the business."
                        className="text-[#98A2B3]"
                    />
                </div>

                <div className="mb-7 flex flex-col gap-3 md:flex-row md:items-center md:gap-8">
                    <button
                        type="button"
                        onClick={() => selectMode(CONTROL_PERSON_MODES.EXISTING)}
                        className="flex items-center gap-1.5 text-[#101828]"
                    >
                        <CheckCircleIcon
                            className={`size-5 ${
                                mode === CONTROL_PERSON_MODES.EXISTING ? "" : "*:fill-[#DBDFE5]"
                            }`}
                        />
                        <span>Use an existing director</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => selectMode(CONTROL_PERSON_MODES.NEW)}
                        className="flex items-center gap-1.5 text-[#667085]"
                    >
                        <CheckCircleIcon
                            className={`size-5 ${
                                mode === CONTROL_PERSON_MODES.NEW ? "" : "*:fill-[#DBDFE5]"
                            }`}
                        />
                        <span>Add a new control person</span>
                    </button>
                </div>

                {errorMessage && <ErrorMessage message={errorMessage} />}

                {mode === CONTROL_PERSON_MODES.EXISTING && (
                    <div className="mb-8">
                        <p className="aku-body-small-medium mb-1 text-[#101828]">Director</p>
                        <Dropdown
                            value={selectedDirectorKey}
                            options={directorOptions}
                            onChange={setSelectedDirectorKey}
                            placeholder={
                                loading
                                    ? "Loading directors…"
                                    : directors.length
                                      ? "Select director"
                                      : "No directors found"
                            }
                            readOnly={loading || directors.length === 0}
                            inputBg="#EBEDF9"
                            className="!mt-0"
                            dropdownClassName="max-h-72 !z-[9999]"
                        />
                        {selectedDirector && (
                            <p className="mt-2 text-sm text-[#6F739B]">
                                This person would be saved as your control person (UBO)
                            </p>
                        )}
                    </div>
                )}

                {formVisible && (
                    <div className="rounded-lg bg-white !p-6">
                        <h2 className="aku-body-normal-medium text-[#101828]">
                            {mode === CONTROL_PERSON_MODES.EXISTING
                                ? "Update director’s information"
                                : "Add new control person"}
                        </h2>

                        <div className="mt-6">
                            <p className="aku-body-small-medium mb-1 text-[#383B56]">Name</p>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <LockedField locked={isFieldLocked("firstName")}>
                                    <Input
                                        value={form.firstName}
                                        onChange={(value) => updateField("firstName", value)}
                                        placeholder="First name"
                                        className="!mt-0"
                                        disabled={isFieldLocked("firstName")}
                                    />
                                </LockedField>
                                <LockedField locked={isFieldLocked("lastName")}>
                                    <Input
                                        value={form.lastName}
                                        onChange={(value) => updateField("lastName", value)}
                                        placeholder="Last name"
                                        className="!mt-0"
                                        disabled={isFieldLocked("lastName")}
                                    />
                                </LockedField>
                            </div>
                        </div>

                        <div className="mt-6">
                            <LockedField locked={isFieldLocked("email")}>
                                <Input
                                    type="email"
                                    label="Email address"
                                    labelClassName="aku-body-small-medium !mb-1 !mt-0 text-[#101828]"
                                    value={form.email}
                                    onChange={(value) => updateField("email", value)}
                                    placeholder="Enter email address"
                                    disabled={isFieldLocked("email")}
                                />
                            </LockedField>
                        </div>

                        <div className="mt-6">
                            <p className="aku-body-small-medium mb-1 text-[#383B56]">Phone number</p>
                            <div className="flex gap-2">
                                <div className="w-[138px] shrink-0">
                                    <LockedField locked={isFieldLocked("phoneCountry")}>
                                        <Dropdown
                                            value={form.phoneCountry}
                                            onChange={(value) => updateField("phoneCountry", value)}
                                            options={phoneCountryOptions}
                                            className="!mt-0"
                                            dropdownClassName="!min-w-[300px]"
                                            searchable
                                            searchPlaceholder="Search country"
                                            placeholder={loading ? "..." : "--"}
                                            readOnly={isFieldLocked("phoneCountry")}
                                        />
                                    </LockedField>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <LockedField locked={isFieldLocked("phoneNumber")}>
                                        <div
                                            className={`flex h-[56px] items-center rounded-lg px-4 ${
                                                isFieldLocked("phoneNumber")
                                                    ? "bg-[#F8FAFC]"
                                                    : "bg-[#f1f5f9]"
                                            }`}
                                        >
                                            <span
                                                className={`mr-3 ${
                                                    isFieldLocked("phoneNumber")
                                                        ? "text-[#98A2B3]"
                                                        : "text-[#383B56]"
                                                }`}
                                            >
                                                {selectedPhoneCountry?.dialCode || ""}
                                            </span>
                                            <input
                                                type="tel"
                                                value={form.phoneNumber}
                                                onChange={(event) =>
                                                    updateField(
                                                        "phoneNumber",
                                                        event.target.value.replace(/[^\d]/g, "")
                                                    )
                                                }
                                                placeholder="905 555 0123"
                                                disabled={isFieldLocked("phoneNumber")}
                                                className={`min-w-0 flex-1 bg-transparent outline-none ${
                                                    isFieldLocked("phoneNumber")
                                                        ? "text-[#98A2B3] disabled:opacity-100 disabled:[-webkit-text-fill-color:#98A2B3]"
                                                        : "text-[#101828]"
                                                }`}
                                            />
                                        </div>
                                    </LockedField>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-1 items-start gap-2 sm:grid-cols-2">
                            <div>
                                <LockedField locked={isFieldLocked("title")}>
                                    <Input
                                        label="Title"
                                        labelClassName="aku-body-small-medium !mb-1 !mt-0 text-[#101828]"
                                        value={form.title}
                                        onChange={(value) => updateField("title", value)}
                                        placeholder="e.g. CEO, MD, CFO, etc."
                                        className="!mt-0"
                                        disabled={isFieldLocked("title")}
                                    />
                                </LockedField>
                            </div>
                            <div>
                                <LockedField locked={isFieldLocked("ownershipShare")}>
                                    <Input
                                        label="Ownership share (%)"
                                        labelClassName="aku-body-small-medium !mb-1 !mt-0 text-[#101828]"
                                        value={form.ownershipShare}
                                        onChange={(value) =>
                                            updateField(
                                                "ownershipShare",
                                                normalizeOwnershipInput(value)
                                            )
                                        }
                                        placeholder="25"
                                        className="!mt-0"
                                        disabled={isFieldLocked("ownershipShare")}
                                        inputClassName={
                                            ownershipError
                                                ? "!bg-[#FFF1F3] !shadow-[inset_0_0_0_1000px_#FFF1F3] !ring-1 !ring-red-400"
                                                : ""
                                        }
                                    />
                                </LockedField>
                                {ownershipError ? (
                                    <p className="mt-1 text-xs text-red-500">{ownershipError}</p>
                                ) : (
                                    <p className="mt-1 text-xs text-[#667085]">
                                        {isNewControlPerson
                                            ? `${availableOwnership}% ownership available. Minimum ownership is 25%.`
                                            : "Ownership share must be between 25% and 100%."}
                                    </p>
                                )}
                            </div>
                        </div>

                        {requiresBvn && (
                            <div className="mt-6">
                                <LockedField locked={isFieldLocked("bvn")}>
                                    <Input
                                        label="Bank verification number (BVN)"
                                        labelClassName="aku-body-small-medium !mb-1 !mt-0 text-[#101828]"
                                        value={form.bvn}
                                        onChange={(value) =>
                                            updateField("bvn", String(value).replace(/[^\d]/g, ""))
                                        }
                                        placeholder="Enter 11-digit BVN"
                                        disabled={isFieldLocked("bvn")}
                                    />
                                </LockedField>
                                {form.bvn && !/^\d{11}$/.test(form.bvn) && (
                                    <p className="mt-1 text-sm text-red-500">
                                        BVN must be exactly 11 digits.
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="mt-6 grid grid-cols-1 items-start gap-2 sm:grid-cols-2">
                            <div>
                                <p className="aku-body-small-medium mb-1 text-[#383B56]">Gender</p>
                                <LockedField locked={isFieldLocked("gender")}>
                                    <Dropdown
                                        value={form.gender}
                                        onChange={(value) => updateField("gender", value)}
                                        options={genderOptions}
                                        placeholder="Select gender"
                                        className="!mt-0"
                                        readOnly={isFieldLocked("gender")}
                                    />
                                </LockedField>
                            </div>
                            <div>
                                <LockedField locked={isFieldLocked("dateOfBirth")}>
                                    <Input
                                        type="date"
                                        label="Date of birth"
                                        labelClassName="aku-body-small-medium !mb-1 !mt-0 text-[#101828]"
                                        value={form.dateOfBirth}
                                        onChange={(value) => updateField("dateOfBirth", value)}
                                        max={new Date(Date.now() - 86400000)
                                            .toISOString()
                                            .slice(0, 10)}
                                        min="1900-01-01"
                                        className="!mt-0"
                                        placeholder="Select date"
                                        disabled={isFieldLocked("dateOfBirth")}
                                    />
                                </LockedField>
                            </div>
                        </div>

                        <div className="mt-6">
                            <div className="mb-1 flex items-center justify-between">
                                <p className="aku-body-small-medium text-[#101828]">
                                    Identification document
                                </p>
                                <button
                                    type="button"
                                    className="flex items-center gap-1 text-sm text-[#6F00FF]"
                                    onClick={() => setIsSampleModalOpen(true)}
                                >
                                    See sample
                                    <span className="material-icons text-base">chevron_right</span>
                                </button>
                            </div>
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                <LockedField locked={isFieldLocked("identificationType")}>
                                    <Dropdown
                                        value={form.identificationType}
                                        onChange={(value) =>
                                            updateField("identificationType", value)
                                        }
                                        options={identificationOptions}
                                        className="!mt-0"
                                        readOnly={isFieldLocked("identificationType")}
                                    />
                                </LockedField>
                                <LockedField locked={isFieldLocked("identificationNumber")}>
                                    <Input
                                        value={form.identificationNumber}
                                        onChange={(value) =>
                                            updateField("identificationNumber", value)
                                        }
                                        placeholder={`Enter ${selectedIdentificationLabel.toLowerCase()} ID number`}
                                        className="!mt-0"
                                        disabled={isFieldLocked("identificationNumber")}
                                    />
                                </LockedField>
                            </div>

                            <div
                                className={`mt-6 grid grid-cols-1 gap-2 ${
                                    needsBackDocument ? "md:grid-cols-2" : ""
                                }`}
                            >
                                <LockedField locked={isFieldLocked("identificationDocumentFront")}>
                                    <UploadInputField
                                        label={
                                            needsBackDocument
                                                ? "Upload front side"
                                                : "Upload passport"
                                        }
                                        uploadTitle={
                                            needsBackDocument
                                                ? "Upload front side"
                                                : `Upload ${selectedIdentificationLabel.toLowerCase()}`
                                        }
                                        hideLabel
                                        fileKey="usd-control-person-id-front"
                                        fileState={{
                                            file: form.identificationDocumentFront,
                                            loading: uploadingSide === "front",
                                            uploaded: hasFrontDocument,
                                            progress: hasFrontDocument ? 100 : 0,
                                        }}
                                        onFileChange={(event) =>
                                            handleDocumentUpload(event, "front")
                                        }
                                        onRemove={() =>
                                            updateField("identificationDocumentFront", null)
                                        }
                                        disabled={isFieldLocked("identificationDocumentFront")}
                                        inputBg="#f1f5f9"
                                        uploadedBg="#f1f5f9"
                                        iconBg="#DCCBFF"
                                    />
                                </LockedField>
                                {needsBackDocument && (
                                    <LockedField
                                        locked={isFieldLocked("identificationDocumentBack")}
                                    >
                                        <UploadInputField
                                            label="Upload back side"
                                            uploadTitle="Upload back side"
                                            hideLabel
                                            fileKey="usd-control-person-id-back"
                                            fileState={{
                                                file: form.identificationDocumentBack,
                                                loading: uploadingSide === "back",
                                                uploaded: hasBackDocument,
                                                progress: hasBackDocument ? 100 : 0,
                                            }}
                                            onFileChange={(event) =>
                                                handleDocumentUpload(event, "back")
                                            }
                                            onRemove={() =>
                                                updateField("identificationDocumentBack", null)
                                            }
                                            disabled={isFieldLocked("identificationDocumentBack")}
                                            inputBg="#f1f5f9"
                                            uploadedBg="#f1f5f9"
                                            iconBg="#DCCBFF"
                                        />
                                    </LockedField>
                                )}
                            </div>
                            <div className="mt-3 flex items-center gap-2 text-[#475467]">
                                <InfoCircle size="20" color="#2C53F2" variant="Bold" />
                                <p className="text-sm">
                                    All information on the{" "}
                                    {needsBackDocument ? "documents" : "document"} should be clear
                                    and readable
                                </p>
                            </div>
                        </div>

                        <div className="mt-6">
                            <LockedField locked={isFieldLocked("streetAddress")}>
                                <Input
                                    type="textarea"
                                    label="Residential address"
                                    labelClassName="aku-body-small-medium !mb-1 !mt-0 text-[#101828]"
                                    value={form.streetAddress}
                                    onChange={(value) => updateField("streetAddress", value)}
                                    placeholder="Street address"
                                    disabled={isFieldLocked("streetAddress")}
                                />
                            </LockedField>
                            <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-3">
                                <LockedField locked={isFieldLocked("state")}>
                                    <Dropdown
                                        value={form.state}
                                        onChange={(value) => updateField("state", value)}
                                        options={stateOptions}
                                        placeholder="State"
                                        className="!mt-0"
                                        dropdownClassName="max-h-56"
                                        readOnly={isFieldLocked("state")}
                                    />
                                </LockedField>
                                <LockedField locked={isFieldLocked("city")}>
                                    <Input
                                        value={form.city}
                                        onChange={(value) => updateField("city", value)}
                                        placeholder="City"
                                        className="!mt-0"
                                        disabled={isFieldLocked("city")}
                                    />
                                </LockedField>
                                <LockedField locked={isFieldLocked("postalCode")}>
                                    <Input
                                        value={form.postalCode}
                                        onChange={(value) => updateField("postalCode", value)}
                                        placeholder="ZIP / Postal Code"
                                        className="!mt-0"
                                        disabled={isFieldLocked("postalCode")}
                                    />
                                </LockedField>
                            </div>
                        </div>

                        {isNewControlPerson && (
                            <div className="mt-6">
                                <p className="aku-body-small-medium mb-1 text-[#101828]">
                                    Is this person also a director of the business?
                                </p>
                                <div className="flex items-center gap-8">
                                    <button
                                        type="button"
                                        className="flex items-center gap-2 text-base text-[#101828]"
                                        onClick={() => updateField("isDirector", true)}
                                    >
                                        <CheckCircleIcon
                                            className={`size-5 ${
                                                form.isDirector ? "" : "*:fill-[#DBDFE5]"
                                            }`}
                                        />
                                        Yes
                                    </button>
                                    <button
                                        type="button"
                                        className="flex items-center gap-2 text-base text-[#667085]"
                                        onClick={() => updateField("isDirector", false)}
                                    >
                                        <CheckCircleIcon
                                            className={`size-5 ${
                                                form.isDirector ? "*:fill-[#DBDFE5]" : ""
                                            }`}
                                        />
                                        No
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-8 flex justify-end gap-4">
                    <button
                        type="button"
                        onClick={() => (onBack ? onBack() : navigate(-1))}
                        disabled={saving}
                        className="h-12 rounded-lg px-6 text-[#101828]"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={handleContinue}
                        disabled={!isFormValid || saving || Boolean(uploadingSide)}
                        className={`flex h-12 min-w-[120px] items-center justify-center rounded-lg px-6 text-white ${
                            isFormValid && !saving && !uploadingSide
                                ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                : "cursor-not-allowed bg-[#D7C5FF]"
                        }`}
                    >
                        {saving ? <Loader /> : "Continue"}
                    </button>
                </div>

                <SampleDocumentModal
                    isOpen={isSampleModalOpen}
                    onClose={() => setIsSampleModalOpen(false)}
                    title={`${selectedIdentificationLabel} sample`}
                    tabs={[]}
                    documentSrc={identificationSampleDocuments[form.identificationType]}
                />
            </div>
        </OnboardingLayout>
    );
}

export default BusinessContact;
